import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Trash2, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/fabricData";
import {
  getImportFiles, getImportFile, deleteImportFile,
  getExportFiles, deleteExportFile, formatApiError,
} from "@/lib/api";

export function CustomerFileHistory({ token, reloadToken }) {
  const [imports, setImports] = useState([]);
  const [exports, setExports] = useState([]);
  const [preview, setPreview] = useState(null); // {filename, records}

  const load = async () => {
    try {
      const [imp, exp] = await Promise.all([getImportFiles(token), getExportFiles(token)]);
      setImports(imp); setExports(exp);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reloadToken]);

  const openPreview = async (f) => {
    try { setPreview(await getImportFile(token, f.id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delImport = async (f) => {
    if (!window.confirm(`Remove "${f.filename}" from import history? The imported customers will NOT be deleted.`)) return;
    try { await deleteImportFile(token, f.id); toast.success("Import record removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delExport = async (f) => {
    if (!window.confirm(`Remove "${f.filename}" from export history?`)) return;
    try { await deleteExportFile(token, f.id); toast.success("Export record removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="customer-file-history">
      {/* Import history */}
      <div className="border border-white/10 bg-[#0A0A0A]">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-[#141414]">
          <Upload className="h-4 w-4 text-white/60" />
          <span className="text-sm font-medium text-white">Imported Files</span>
          <span className="text-xs text-white/40">({imports.length})</span>
        </div>
        <div className="divide-y divide-white/5 max-h-72 overflow-auto" data-testid="import-history-list">
          {imports.length === 0 && <p className="px-4 py-6 text-sm text-white/40">No imports yet.</p>}
          {imports.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`import-file-${f.id}`}>
              <button onClick={() => openPreview(f)} className="flex-1 min-w-0 text-left group" data-testid={`import-file-preview-${f.id}`}>
                <p className="text-sm text-white truncate group-hover:underline">{f.filename}</p>
                <p className="text-[11px] text-white/50">{formatDateTime(f.imported_at)} · <span className="text-emerald-400">{f.count} imported</span>{f.duplicate_count ? <span className="text-red-400"> · {f.duplicate_count} dup</span> : null}</p>
              </button>
              <Button onClick={() => openPreview(f)} variant="outline" className="rounded-none h-8 w-8 p-0 border-white/20 bg-transparent text-white hover:bg-white hover:text-black" data-testid={`import-file-view-${f.id}`}><Eye className="h-3.5 w-3.5" /></Button>
              <Button onClick={() => delImport(f)} variant="outline" className="rounded-none h-8 w-8 p-0 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white" data-testid={`import-file-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Export history */}
      <div className="border border-white/10 bg-[#0A0A0A]">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-[#141414]">
          <Download className="h-4 w-4 text-white/60" />
          <span className="text-sm font-medium text-white">Exported Files</span>
          <span className="text-xs text-white/40">({exports.length})</span>
        </div>
        <div className="divide-y divide-white/5 max-h-72 overflow-auto" data-testid="export-history-list">
          {exports.length === 0 && <p className="px-4 py-6 text-sm text-white/40">No exports yet.</p>}
          {exports.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`export-file-${f.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{f.filename}</p>
                <p className="text-[11px] text-white/50">{formatDateTime(f.exported_at)}{f.count ? ` · ${f.count} records` : ""}</p>
              </div>
              <Button onClick={() => delExport(f)} variant="outline" className="rounded-none h-8 w-8 p-0 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white" data-testid={`export-file-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-2xl rounded-none flex flex-col max-h-[85dvh]" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl tracking-tight text-white truncate">{preview?.filename}</DialogTitle>
            <DialogDescription className="text-white/50">{preview ? `${preview.records?.length || 0} customers were brought in from this file` : ""}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border border-white/10">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-white/40 bg-[#141414] sticky top-0">
              <span className="col-span-4">Name</span><span className="col-span-3">Phone</span><span className="col-span-2">Place</span><span className="col-span-3">Remark</span>
            </div>
            {(preview?.records || []).map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm text-white border-t border-white/5" data-testid={`import-preview-row-${i}`}>
                <span className="col-span-4 truncate">{r.name}</span>
                <span className="col-span-3 truncate text-white/70">{r.phone || "—"}</span>
                <span className="col-span-2 truncate text-white/70">{r.place || "—"}</span>
                <span className="col-span-3 truncate text-white/70">{r.remark || "—"}</span>
              </div>
            ))}
            {preview && (preview.records || []).length === 0 && <p className="px-3 py-6 text-sm text-white/40">No new customers were imported from this file.</p>}
          </div>
          <Button onClick={() => setPreview(null)} className="shrink-0 rounded-none h-10 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider" data-testid="import-preview-close"><X className="h-4 w-4 mr-1" /> Close</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
